import BaseMap from '../../../components/base-map/base-map';
import {LayerPropsWithType, Layers} from '../../../components/base-map/layers/types';
import {useContext, useEffect, useMemo, useRef, useState, memo} from 'react';
import {BBox, FeatureCollection} from 'geojson';
import mapboxConfig from '../../../insight-container/map/map-libs/mapbox-config';
import {NetworkContext} from '../lib/context';
import {CirclePaint, MapMouseEvent} from 'mapbox-gl';
import {observer} from 'mobx-react';
import bbox from '@turf/bbox';
import {BBox2d} from '@turf/helpers/dist/js/lib/geojson';
import Spinner from '../../../components/spinner';
import {safeConsoleError} from '../../../lib/sanitize';
import handleError from '../../../lib/error';
import {Checkbox, IconButton} from '@carbon/react';
import {CheckboxCheckedFilled, Group, Hospital, Icon, LocationFilled, Maximize} from '@carbon/icons-react';
import {convertDataToGeoJSONPoints} from '../../../lib/data';
import {PercentFormatter} from '../../../lib/formatter';
import './network-map.scss';
import {SaveRosterButton} from './additional-providers-table/save-roster-button';
import {bboxPolygon, booleanContains} from '@turf/turf';
import {MapRef} from 'react-map-gl';
import ErrorDisplay from './error-display';

const INVALID_COLOR = 'rgba(205, 77, 81, 0.6)';
const VALID_COLOR = 'rgba(0, 120, 212, 0.6)';
const NULL_COLOR = 'rgba(79, 93, 117, 0.6)';
const PROVIDER_COLOR = 'rgba(255, 222, 102, 1)';
const ADDITIONAL_PROVIDER_COLOR = 'rgba(150, 215, 237, 1)';
const SELECTED_PROVIDER_COLOR = 'rgba(214, 183, 255, 1)';
const PROPERTY_TO_MAP = 'impacted_flag';
const CONTINENTAL_US_BOUNDS: BBox2d = [-124.7844079, 24.7433195, -66.9513812, 49.3457868];
const DISRUPTION_ANALYSIS_PROVIDER_TAB_INDEX = 3;
const CLUSTER_PROPERTIES = ['full_name', 'npi'];

const DEFAULT_LAYER_STYLE: CirclePaint = {
    'circle-radius': .25, 
    'circle-color': ['match', ['get', PROPERTY_TO_MAP], '1', VALID_COLOR, '0', INVALID_COLOR, NULL_COLOR ] //['interpolate',['linear'],['get', PROPERTY_TO_MAP],0,INVALID_COLOR,1,VALID_COLOR]
};

const INTERACTIVE_MAP_LAYER_IDS = ['layer-2', 'layer-3', 'layer-4'];

function createBaseLayers(patientData: FeatureCollection, providerData: FeatureCollection, cluster = true): {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] {
    return [{name: 'Patients', icon: Group, layer: {
        id: `layer-1`,
        data: patientData,
        layerType: Layers.circle,
        paint: DEFAULT_LAYER_STYLE
    }},
    {name: 'Providers', icon: Hospital, layer: {
        id: `layer-2`,
        data: providerData,
        layerType: cluster ? Layers.clusteredPoints : Layers.point,
        paint: {'icon-color': PROVIDER_COLOR},
        clusterProperties: CLUSTER_PROPERTIES
    }}];
}

function createAdditionalProviderLayers(additionalProviders: FeatureCollection, selectedAdditionalProviders: FeatureCollection, cluster = true): {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] {
    return [{name: 'Additional Providers', icon: Hospital, layer: {id: `layer-3`,
        data: additionalProviders,
        layerType: cluster ? Layers.clusteredPoints : Layers.point,
        paint: {'icon-color': ADDITIONAL_PROVIDER_COLOR},
        clusterProperties: CLUSTER_PROPERTIES
    }},
    {name: 'Selected Additional Providers', icon: Hospital, layer: {id: `layer-4`,
        data: selectedAdditionalProviders,
        layerType: Layers.point,
        paint: {'icon-color': SELECTED_PROVIDER_COLOR},
        layout: {'icon-image': 'check-pin'}
    }}];
}

interface NetworkMapProps {
    setSaveRosterModalOpen: (open: boolean) => void;
}

const DisruptionAnalysisNetworkMap = memo(observer(({setSaveRosterModalOpen}: NetworkMapProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [popupOptions, setPopupOptions] = useState<any>();
    const [disabledLayers, setDisabledLayers] = useState<string[]>([]);
    const networkStore = useContext(NetworkContext);
    const divRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapRef | null>(null);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [currentBounds, setCurrentBounds] = useState<BBox2d>(CONTINENTAL_US_BOUNDS);
    const [cluster, setCluster] = useState<boolean>(false);
    const revisionId = networkStore.revisionId ?? 0;

    const requestFullScreen = () => {
        const element = divRef.current;
        if (!element) return;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (element.requestFullscreen) {
            element.requestFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Reset bounds when specialty filter changes to allow map to zoom to new data
    useEffect(() => {
        setCurrentBounds(CONTINENTAL_US_BOUNDS);
    }, [networkStore.disruptionAnalysisSpecialtyFilter]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        networkStore.disruptionAnalysisLoadMapGeoJson(controller)
            .catch((err) => handleError(err, setError))
            .finally(() => {
                if (networkStore.patientGeoJson && networkStore.providerGeoJson)
                    setLoading(false);
            });

        return () => controller.abort();
    }, [networkStore.appliedDisruptionAnalysisFilter, networkStore.disruptionAnalysisSpecialtyFilter, networkStore.runId, networkStore.revisionId]);

    function onHoverCB(features: Array<any>) {
        if (!features || !features.length) {
            setPopupOptions(null);
            return;
        }
        const fProps = features[0].properties;
        if (fProps.cluster) {
            const propertiesToShow: Record<string, string[]> = CLUSTER_PROPERTIES.reduce((prev, curr) => {
                return {...prev, [curr]: fProps[curr]?.split('|')};
            }, {});
            setPopupOptions({name: <div className='text-background-gray-900'>
                Providers: {fProps.point_count}
                {fProps.point_count <= 10 && Array.from({length: fProps.point_count}, (_, i) => i).map((index) => 
                    <ul key={index} className='border-b py-1'>{CLUSTER_PROPERTIES.map((p, i) => <li key={p} className={i === 0 ? 'font-semibold' : ''}>{propertiesToShow[p][index]}</li>)}</ul>
                )}
            </div>, latitude: features[0].geometry.coordinates[1], longitude: features[0].geometry.coordinates[0]});
            return;
        }

        const {full_name, npi, practice_location_address_postal_code, pct_visit_count, pct_allowed_amount} = fProps;

        const popupElement = <ul className='text-background-gray-900'>
            <li><strong>{full_name}</strong></li>
            <li>NPI: {npi}</li>
            <li>Zipcode: {practice_location_address_postal_code}</li>
            {pct_visit_count && <li>Utilization: {new PercentFormatter(0.1, null).formatValue(pct_visit_count)}</li>}
            {pct_allowed_amount && <li>Allowed Impact (Actual): {new PercentFormatter(0.1, null).formatValue(pct_allowed_amount)}</li>}
        </ul>;
        setPopupOptions({name: popupElement, latitude: fProps.latitude, longitude: fProps.longitude});
    }

    function onClickCB(event: MapMouseEvent) {
        const features = event.features || [];

        if (features && features.length === 0) {
            return;
        }
        const feature = features[0];
        const isClustured = feature.properties!.cluster;
        const key = feature.properties!.id || feature.properties!['provider_address_key'];
        if(isClustured) {
            mapRef.current?.getMap().zoomIn();
        }
        if (feature.source === 'layer-2') {
            networkStore.existingProviderQueuedForDeletion = key;
        } else if (feature.source === 'layer-3' && !isClustured) {
            networkStore.existingProviderQueuedForDeletion = null;
            if (networkStore.selectedAdditionalProviderKeys.includes(key)) {
                networkStore.deselectAdditionalProviders([key]);
            } else if (networkStore.deselectedExistingProviderKeys.includes(key)) {
                networkStore.selectExistingProviders([key]);
            } else {
                networkStore.selectAdditionalProviders([key]);
            }
        }
    }

    function setLayerEnabled(layerId: string, checked: boolean) {
        setDisabledLayers(checked ? disabledLayers.filter(l => l !== layerId) : disabledLayers.concat(layerId));
    }

    const bounds: BBox = useMemo(() => {
        // Helper function to validate bounds are within valid coordinate ranges
        const isValidBounds = (bounds: BBox2d): boolean => {
            const [minLng, minLat, maxLng, maxLat] = bounds;
            return !isNaN(minLng) && !isNaN(minLat) && !isNaN(maxLng) && !isNaN(maxLat) &&
                   isFinite(minLng) && isFinite(minLat) && isFinite(maxLng) && isFinite(maxLat) &&
                   minLat >= -90 && minLat <= 90 &&
                   maxLat >= -90 && maxLat <= 90 &&
                   minLng >= -180 && minLng <= 180 &&
                   maxLng >= -180 && maxLng <= 180;
        };

        let newBounds: BBox2d = CONTINENTAL_US_BOUNDS as BBox2d;

        // Try to calculate bounds from data, falling back to continental US if invalid
        if (networkStore.patientGeoJson && networkStore.patientGeoJson.features.length > 0) {
            const calculatedBounds = bbox(networkStore.patientGeoJson) as BBox2d;
            if (isValidBounds(calculatedBounds)) {
                newBounds = calculatedBounds;
            }
        } else if (networkStore.providerGeoJson && networkStore.providerGeoJson.features.length > 0) {
            const calculatedBounds = bbox(networkStore.providerGeoJson) as BBox2d;
            if (isValidBounds(calculatedBounds)) {
                newBounds = calculatedBounds;
            }
        }

        const currentBoundsIsSmaller = booleanContains(bboxPolygon(newBounds), bboxPolygon(currentBounds));
        if (!currentBoundsIsSmaller) {
            setCurrentBounds(newBounds);
            return newBounds;
        }

        return currentBounds;
    }, [networkStore.patientGeoJson, networkStore.providerGeoJson]);

    const allLayers = useMemo(() => {
        let newLayers: {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] = [];

        try {
            if (!(networkStore.patientGeoJson && networkStore.providerGeoJson))
                return newLayers;

            newLayers = newLayers.concat(createBaseLayers(networkStore.patientGeoJson, networkStore.providerGeoJson, cluster));

            if (networkStore.localProviders.length && networkStore.activeTab === DISRUPTION_ANALYSIS_PROVIDER_TAB_INDEX)
                newLayers = newLayers.concat(
                    createAdditionalProviderLayers(
                        convertDataToGeoJSONPoints(networkStore.nonSelectedAdditionalProviders, 'latitude', 'longitude'),
                        convertDataToGeoJSONPoints(networkStore.selectedAdditionalProviders, 'latitude', 'longitude'),
                        cluster));

            return newLayers;
        } catch (error) {
            safeConsoleError('Error creating map layers:', error);
            setError('Unable to display map data. Please try refreshing or selecting a different specialty.');
            return [];
        }
    }, [networkStore.patientGeoJson, networkStore.providerGeoJson, networkStore.localProviders, networkStore.selectedAdditionalProviders, networkStore.activeTab, cluster]);

    const layers = useMemo(() => 
        allLayers.filter(l => l.layer.data?.features.length).map(l => {
            const isDisabled = disabledLayers.includes(l.layer.id.toString());
            let layerType = l.layer.layerType;
            if (layerType !== Layers.circle)
                layerType = isDisabled || !cluster ? Layers.point : Layers.clusteredPoints;

            return ({...l.layer, layout: {...l.layer.layout, 'visibility': isDisabled ? 'none' : 'visible' as 'none' | 'visible'}, layerType: layerType});
        })
    , [allLayers, disabledLayers, cluster]);

    const interactiveMapLayerIds = useMemo(() => [...layers.filter(l => INTERACTIVE_MAP_LAYER_IDS.includes(l.id as string)).map(l => l.id as string)], [layers]);
    const handleMapRefReady = (ref: MapRef | null) => {
        mapRef.current = ref;
    };

    return <div className="cds--g90 w-full h-full relative flex justify-stretch" ref={divRef} >
        {loading && <div className="flex items-center absolute w-full h-full bg-background-gray-300 bg-opacity-20 justify-center z-[150]">
            <Spinner /> <span className="ml-2 text-xl font-semibold text-background-gray-300">Loading Map Data...</span>
        </div>}
        {error && (
            <div className="absolute inset-0 z-[150]">
                <ErrorDisplay error={error} title='Unable to Load Map Data' fullWidth />
            </div>
        )}
        {!error &&
            <>
                <BaseMap style={{width: '100%', height: '100%'}}
                    layers={layers}
                    viewState={{bounds: bounds as BBox2d, fitBoundsOptions: {padding: 20}}}
                    mapStyle={mapboxConfig.darkStyle} 
                    interactiveLayers={interactiveMapLayerIds}
                    popupOptions={popupOptions}
                    onHoverCB={onHoverCB}
                    onClickCB={onClickCB}
                    onMapRefReady={handleMapRefReady} />
                <div className=" absolute top-2 right-2 flex flex-col gap-2 z-[149]">
                    <div className='bg-background-gray-700 text-background-gray-100 p-2 flex flex-col gap-1' data-testid="layers-panel">
                        <div className='text-base font-semibold pb-1'>Layers</div>
                        {allLayers.map((l, i) => 
                            <div key={i} data-testid={`layer-checkbox-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                <Checkbox className='!text-xs' checked={!disabledLayers.includes(l.layer.id.toString())} id={i.toString()} labelText={l.name} onChange={(_, {checked}) => setLayerEnabled(l.layer.id.toString(), checked)} />
                            </div>)}
                    </div>
                    <div className='bg-background-gray-700 text-background-gray-100 p-2'>
                        <Checkbox id='cluster' className='!text-xs justify-items-center' checked={cluster} labelText={'Enable Pin Grouping'} onChange={(_, {checked}) => setCluster(checked)} />
                    </div>
                </div>
                <div className="flex flex-row items-center absolute bottom-2 left-0 right-0 m-auto w-[fit-content] max-w-[calc(100%-1rem)] bg-background-gray-700 text-background-gray-100 justify-around z-[149] p-2 gap-3 flex-wrap">
                    {networkStore.disruptionAnalysisSpecialtyFilter ? <>
                        <div className='inline-flex text-xs gap-2 bg-opacity-100'>
                            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={VALID_COLOR} className='w-4' >
                                <circle cx="50" cy="50" r="50" />
                            </svg>
                            <span className='text-nowrap'>Impacted</span>
                        </div>
                        <div className='inline-flex text-xs gap-2 bg-opacity-100'>
                            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={INVALID_COLOR} className='w-4' >
                                <circle cx="50" cy="50" r="50" />
                            </svg>
                            <span className='text-nowrap'>Not Impacted</span>
                        </div>
                    </> : <div className='inline-flex text-xs gap-2'>
                        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={NULL_COLOR} className='w-4' >
                            <circle cx="50" cy="50" r="50" />
                        </svg>
                        <span className='text-nowrap'>Patients</span>
                    </div>}
                    <div className='inline-flex text-xs gap-2'>
                        <LocationFilled style={{'color': PROVIDER_COLOR}} />
                        <span className='text-nowrap'>Current Providers</span>
                    </div>
                    {!!networkStore.localProviders.length && networkStore.activeTab === DISRUPTION_ANALYSIS_PROVIDER_TAB_INDEX && <div className='inline-flex text-xs gap-2'>
                        <LocationFilled style={{'color': ADDITIONAL_PROVIDER_COLOR}} />
                        <span className='text-nowrap'>Additional Providers</span>
                    </div>}
                    {!!networkStore.selectedAdditionalProviders.length && networkStore.activeTab === DISRUPTION_ANALYSIS_PROVIDER_TAB_INDEX && <div className='inline-flex text-xs gap-2'>
                        <CheckboxCheckedFilled style={{'color': SELECTED_PROVIDER_COLOR}} />
                        <span className='text-nowrap'>Selected Additional Providers</span>
                    </div>}
                </div>
                <div className="absolute bottom-2 right-2 z-[149] flex flex-col items-end gap-2">
                    {isFullscreen && networkStore.selectedAdditionalProviders.length && networkStore.activeTab === DISRUPTION_ANALYSIS_PROVIDER_TAB_INDEX && <div className='bg-background-gray-700 text-background-gray-100 flex flex-col gap-1 p-2'>
                        <div className='text-base font-semibold pb-1'>Selected Providers</div>
                        {networkStore.selectedAdditionalProviders.map((p, i) => <div key={i}>{p.full_name}</div>)}
                        <SaveRosterButton
                            revisionId={revisionId}
                            setModalOpen={setSaveRosterModalOpen}
                            onUpdate={async () => {
                                try {
                                    await networkStore.updateRoster(new AbortController());
                                } catch (err) {
                                    networkStore.handleApiError(err, 'Failed to Update Roster');
                                }
                            }}
                        />
                    </div>}
                    <IconButton kind='secondary' align='top-end' onClick={requestFullScreen} label='Full Screen'><Maximize /></IconButton>
                </div>
            </>}
    </div>;
}));

DisruptionAnalysisNetworkMap.displayName = 'DisruptionAnalysisNetworkMap';

export default DisruptionAnalysisNetworkMap;